import { AnchorButton, Button, IconSize, Spinner } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { Text, Tooltip, VoiceboxLogo } from 'vet-bones/components';
import { isEqual, isNotEqual } from 'vet-bones/utils';

import { EditableLabel } from 'src/components/EditableLabel';
import { Link } from 'src/components/Link';
import { PROJECT_MAPPING_RUN_SUGGESTIONS_PARAM } from 'src/constants/project';
import {
  TEST_IDS,
  getProjectMappingHeaderTestId,
  getProjectMappingLinkTestId,
} from 'src/constants/testIds';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { Mapping, Resource } from 'src/containers/ProjectDiagram/types/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { useSuggestionContext } from 'src/containers/ProjectMapping/hooks/suggestion';
import {
  useMappingFromRoute,
  usePrevMappingFromRoute,
} from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { useRenameMapping } from 'src/containers/ProjectMapping/hooks/useRenameMapping';
import { useSuggestMappings } from 'src/containers/ProjectMapping/hooks/useSuggestMappings';
import { ProjectClassMappingHeaderFilterMenu } from 'src/containers/ProjectMapping/ProjectClassMappingFilterMenu';
import { connectionSelector } from 'src/selectors/connection';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { classDraftChangesSelector } from 'src/selectors/draftClass';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectNodeDataSelector } from 'src/selectors/projectNodeData';
import { EntityDrawerType } from 'src/store/ui/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectClassMappingHeader: React.VFC = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const history = useHistory();
  const mapping = useMappingFromRoute() as Mapping & { to: string };
  const prevMapping = usePrevMappingFromRoute();

  const { isLoading } = useSuggestionContext();
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const isConnected = useReduxSelector(isConnectedSelector);
  const connection = useReduxSelector(connectionSelector);
  const isAutoMappingSupported = useReduxSelector(
    isStardogFeatureSupportedSelector('isAutoMappingSupported', connection)
  );

  const handleClickClass = useClickModelConceptClassLink();

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );

  const resourceData = useReduxSelector(
    projectNodeDataSelector<Resource>(projectId, mapping.from),
    isEqual
  );

  const classIri = getModelConceptClassValue(mapping.to);
  const classLabel = getLabelForIri(labelsByIri, classIri);

  const suggestMappings = useSuggestMappings({
    classIri,
    connection,
  });

  const {
    addedChildren,
    addedParents,
    isColorIdModified,
    isDescriptionModified,
    isLabelModified,
  } = useReduxSelector(classDraftChangesSelector(projectId, classIri), isEqual);

  const highlighted =
    isDraftDiagram &&
    Boolean(
      addedChildren.size ||
        addedParents.size ||
        isColorIdModified ||
        isDescriptionModified ||
        isLabelModified ||
        (prevMapping
          ? isNotEqual(mapping.text, prevMapping.text)
          : Boolean(mapping.text))
    );

  React.useEffect(() => {
    const { search, pathname } = history.location;
    const params = new URLSearchParams(search);

    if (params.has(PROJECT_MAPPING_RUN_SUGGESTIONS_PARAM) && isConnected) {
      suggestMappings();
      history.replace(pathname);
    }
  }, [suggestMappings, isConnected, history]);

  const handleEditAttribute = () => {
    dispatch(
      uiSlice.actions.changedEntityDrawer({
        projectId,
        drawer: {
          isEditingAttributes: true,
          entityValue: classIri,
          type: EntityDrawerType.CLASS,
        },
      })
    );
  };

  const handleRenameMapping = useRenameMapping(mapping.key);

  let autoMappingTooltip = '';
  if (!isAutoMappingSupported) {
    autoMappingTooltip = copy.components.projectMapping.suggestions.disabled;
  }

  return (
    <div className="sd-project-mapping-header sd-project-mapping-grid--suggestions">
      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--border-right',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted': highlighted,
          }
        )}
        data-testid={getProjectMappingHeaderTestId(0)}
      >
        <div className="sd-project-mapping-header-label">
          <EditableLabel
            onSetValue={handleRenameMapping}
            placeholder={mapping.text}
            value={mapping.text}
          />
          <Link
            onClick={() => {
              handleClickClass({
                text: classLabel,
                value: classIri,
              });
            }}
            testId={getProjectMappingLinkTestId(classIri, 0)}
          >
            <Text ellipsize>{classLabel}</Text>
          </Link>
        </div>
        <ProjectClassMappingHeaderFilterMenu />
        <Button
          data-testid={TEST_IDS.projectMapping.editAttributesButton}
          onClick={handleEditAttribute}
          outlined
          text={copy.components.projectMapping.attributes.edit}
        />
      </div>
      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--resource',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted': highlighted,
          }
        )}
        data-testid={getProjectMappingHeaderTestId(1)}
      >
        <div className="sd-project-mapping-header-label">
          <Text ellipsize>
            <strong>{resourceData?.text}</strong>
          </Text>
          <Text ellipsize>
            {copy.components.projectMapping.resourceTypeLabel(
              resourceData?.type
            )}
          </Text>
        </div>
        <Tooltip content={autoMappingTooltip} disabled={!autoMappingTooltip}>
          <AnchorButton
            data-testid={TEST_IDS.projectMapping.suggestion.button}
            disabled={Boolean(autoMappingTooltip) || !isConnected || isLoading}
            icon={
              isLoading ? (
                <Spinner size={IconSize.STANDARD} />
              ) : (
                <VoiceboxLogo size={IconSize.LARGE} />
              )
            }
            onClick={suggestMappings}
            outlined
            text={copy.components.projectMapping.suggestions.button}
          />
        </Tooltip>
      </div>

      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--score',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted': highlighted,
          }
        )}
        data-testid={getProjectMappingHeaderTestId(2)}
      />
      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--action',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted': highlighted,
          }
        )}
        data-testid={getProjectMappingHeaderTestId(3)}
      />

      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted': highlighted,
          }
        )}
      />
    </div>
  );
};
